import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material'
import { OrgUserRole, User } from 'api/model'
import { SetterOrUpdater, useRecoilValue, useSetRecoilState } from 'recoil'
import ProfilePhoto from 'components/ProfilePhoto'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import ReadOnlyKeyPair from 'components/ReadOnlyKeyPair'
import UserStatus from './UserStatus'
import { SidebarClose } from 'components/JelledIcons'
import FloatingButtonBar from 'components/FloatingButtonBar'
import { authenticatedUserAtom, toastAtom } from 'state'
import RoleRadioGroup from './RoleRadioGroup'
import { orgsUpdateMember } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { getColorForDarkMode } from 'utils'

const UserDetailsSidebar = ({
  user,
  organizationId,
  open,
  onClose,
  onDeleteClicked,
  setOrganizationMembers,
}: {
  user: User | null
  organizationId?: string | null
  open: boolean
  onClose: () => void
  onDeleteClicked: () => void
  setOrganizationMembers: SetterOrUpdater<User[] | null>
}) => {
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()

  const authenticatedUser = useRecoilValue(authenticatedUserAtom)
  const canEdit = user?.id !== authenticatedUser?.id

  const [tabValue, setTabValue] = useState(0)

  const onTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const TabContainer = (props: { children?: React.ReactNode; index: number }) => {
    const { children, index } = props

    return <div hidden={tabValue !== index}>{tabValue === index && <Box mt={4}>{children}</Box>}</div>
  }

  const roleForOrg = user?.profiles.find((up) => up.organization.id === organizationId)?.role

  // We aren't currently deconstructing the org members list into individual atoms (in an atomFamily)
  // so in order to update the local state of the user from props, we copy the value to a useState.
  const [originalUserRole, setOriginalUserRole] = useState(roleForOrg)
  const [userRole, setUserRole] = useState(roleForOrg)
  useEffect(() => {
    setUserRole(roleForOrg)
    setOriginalUserRole(roleForOrg)
  }, [user?.id, roleForOrg])

  const renderContent = () => {
    if (!user) return <div></div>

    const onRoleChange = (role: OrgUserRole) => {
      setUserRole(role)
    }

    return (
      <Box px={isMobile ? 2 : 4} pt={isMobile ? 2 : 0}>
        <Stack direction="row" alignItems="center" spacing={2} mb={4}>
          <ProfilePhoto src={user.picture} size="96px" name={user.name ?? user.email} />
          <Stack spacing={0} flex={1}>
            <Typography variant={isMobile ? 'h4' : 'h2'} sx={{ wordBreak: 'break-all' }}>
              {user.name ?? user.email}
            </Typography>
            <Typography variant={isMobile ? 'pS' : 'pL'}>{user.title}</Typography>
          </Stack>
          <IconButton disabled={!canEdit} onClick={onDeleteClicked}>
            <DeleteOutlinedIcon />
          </IconButton>
        </Stack>

        <Tabs value={tabValue} onChange={onTabChange} textColor="secondary">
          <Tab
            label="Profile"
            sx={{
              '&.Mui-selected': {
                color: getColorForDarkMode(theme),
              },
            }}
          />
          <Tab
            label="App access"
            sx={{
              '&.Mui-selected': {
                color: getColorForDarkMode(theme),
              },
            }}
          />
        </Tabs>

        <TabContainer index={0}>
          <Stack spacing={3}>
            <ReadOnlyKeyPair name="Email" value={user.email} />
            <ReadOnlyKeyPair name="Phone" value={user.phone_number} />
            <ReadOnlyKeyPair name="App access" value={user.status} />
          </Stack>
        </TabContainer>

        <TabContainer index={1}>
          <Stack spacing={4}>
            <RoleRadioGroup isDarkBackdrop={true} role={userRole} onRoleChange={onRoleChange} disabled={!canEdit} />

            <Box>
              <Typography variant="h5" mb={1}>
                Status
              </Typography>
              <UserStatus organizationId={organizationId} user={user} canReinvitePendingUsers={canEdit} />
            </Box>
          </Stack>
        </TabContainer>
      </Box>
    )
  }

  const hasEdits = userRole !== originalUserRole
  const onCancelEdit = () => {
    setUserRole(roleForOrg)
  }

  const setToast = useSetRecoilState(toastAtom)

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const onUpdateClicked = async () => {
    if (!organizationId || !user || !userRole) {
      return
    }

    setIsConfirmDialogOpen(false)

    try {
      setIsUpdating(true)

      await orgsUpdateMember(organizationId, user.id, { role: userRole })
      setToast({ title: 'App access updated successfully', severity: 'success' })
      setOriginalUserRole(userRole)

      // need to update the user's role in the local state
      setOrganizationMembers((members) => {
        if (!members) {
          return null
        }

        return members.map((member) => {
          if (member.id === user.id) {
            return {
              ...member,
              profiles: member.profiles.map((profile) => {
                if (profile.organization.id === organizationId) {
                  return {
                    ...profile,
                    role: userRole,
                  }
                }

                return profile
              }),
            }
          }

          return member
        })
      })
    } catch (error) {
      setToast({ title: 'App access update failed', severity: 'error' })
    } finally {
      setIsUpdating(false)
    }
  }

  if (isMobile && open) {
    return (
      <Stack>
        <Box width="100%" id="UserSidebar-container" pb={10}>
          {renderContent()}
        </Box>

        <Dialog open={isConfirmDialogOpen}>
          <DialogTitle>Do you want to save these changes?</DialogTitle>
          <DialogActions>
            <Button
              color="secondary"
              onClick={() => {
                setIsConfirmDialogOpen(false)
              }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={onUpdateClicked}>
              Yes, save changes
            </Button>
          </DialogActions>
        </Dialog>

        {hasEdits && (
          <FloatingButtonBar
            onCancelClicked={onCancelEdit}
            updateLabel="Update changes"
            onUpdateClicked={() => {
              setIsConfirmDialogOpen(true)
            }}
            isUpdating={isUpdating}
            containerId="UserSidebar-container"
          />
        )}
      </Stack>
    )
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box width={960} id="UserSidebar-container" pb={10}>
        <Stack direction="row" justifyContent="flex-end" pt={1} pr={1}>
          <IconButton
            onClick={onClose}
            sx={{
              path: {
                fill: getColorForDarkMode(theme),
              },
            }}>
            <SidebarClose />
          </IconButton>
        </Stack>
        {renderContent()}
      </Box>

      <Dialog open={isConfirmDialogOpen}>
        <DialogTitle>Do you want to save these changes?</DialogTitle>
        <DialogActions>
          <Button
            color="secondary"
            onClick={() => {
              setIsConfirmDialogOpen(false)
            }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onUpdateClicked}>
            Yes, save changes
          </Button>
        </DialogActions>
      </Dialog>

      {hasEdits && (
        <FloatingButtonBar
          onCancelClicked={onCancelEdit}
          updateLabel="Update changes"
          onUpdateClicked={() => {
            setIsConfirmDialogOpen(true)
          }}
          isUpdating={isUpdating}
          containerId="UserSidebar-container"
        />
      )}
    </Drawer>
  )
}

export default UserDetailsSidebar
